import { FileUp, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { extractValidationFindings } from '@/features/schema/validation'
import type { ValidationFinding } from '@/services/api/schemas'
import { DialogValidationFindings } from './DialogValidationFindings'

export interface ImportSchemaDialogProps {
  open: boolean
  onOpenChange: (o: boolean) => void
  onImport: (payload: unknown) => Promise<void>
}

/**
 * JSON import dialog. Holds local draft state (text + parse/validation errors)
 * and delegates the actual import to the `onImport` callback — it performs no
 * IO itself.
 */
export function ImportSchemaDialog({ open, onOpenChange, onImport }: ImportSchemaDialogProps) {
  const [text, setText] = useState('')
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [findings, setFindings] = useState<ValidationFinding[] | null>(null)

  const handleFile = async (file: File) => {
    try {
      const content = await file.text()
      setText(content)
      setError(null)
    } catch {
      setError('Could not read file')
    }
  }

  const handleImport = async () => {
    setError(null)
    setFindings(null)
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch (e) {
      setError(`Invalid JSON: ${(e as Error).message}`)
      return
    }
    if (!parsed || typeof parsed !== 'object' || !('name' in parsed)) {
      setError('Schema JSON must contain a "name" field')
      return
    }
    setIsBusy(true)
    try {
      await onImport(parsed)
      setText('')
      onOpenChange(false)
    } catch (e) {
      // A validation rejection (422) carries findings — render them inline and
      // keep the dialog open; anything else falls back to a generic message.
      const validationFindings = extractValidationFindings(e)
      if (validationFindings) {
        setFindings(validationFindings)
      } else {
        setError((e as Error).message || 'Import failed')
      }
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-3xl w-[90vw] max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground">Import Schema from JSON</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Paste a schema JSON or upload a file. If the name matches an existing schema, a new
            version will be created.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2 flex-1 min-h-0">
          <div className="flex items-center gap-2 shrink-0">
            <Label
              htmlFor="schema-json-file"
              className="inline-flex items-center gap-2 h-8 px-3 text-xs bg-muted border border-border rounded cursor-pointer hover:bg-muted/70"
            >
              <FileUp className="h-3.5 w-3.5" />
              Upload JSON file
            </Label>
            <input
              id="schema-json-file"
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) handleFile(f)
              }}
            />
            <span className="text-xs text-muted-foreground">or paste below</span>
          </div>
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder='{"name": "MY_SCHEMA", "entity_types": [...], ...}'
            spellCheck={false}
            wrap="off"
            className="bg-muted border-border font-mono text-xs flex-1 min-h-[280px] overflow-auto whitespace-pre resize-none"
          />
          {error && <p className="text-xs text-destructive shrink-0">{error}</p>}
          <DialogValidationFindings findings={findings} />
        </div>
        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border"
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            className="bg-info hover:bg-info/90"
            disabled={isBusy || !text.trim()}
          >
            {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
